/**
 * SSE Location Service
 * Real-time device location updates via Server-Sent Events
 * Replaces polling for efficient, instant updates
 */

import { debugLog } from '../utils/debugLog';
import { withSseAccessToken } from '../utils/sseAuthUrl';

export interface SSEDeviceLocation {
  device_id: string;
  entity_id?: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  source: 'phone' | 'vehicle' | 'drone' | string;
  speed?: number;
  heading?: number;
  accuracy?: number;
  altitude?: number;
  // Enriched fields from device_reference_table
  entity_type?: string;
  course_id?: string;
  device_type?: string;  // 'Phone', 'Vehicle', 'Watch', 'Drone'
  // Staleness fields (computed by backend)
  last_seen_minutes_ago?: number;
  is_stale?: boolean;
  // LiveKit A/V presence
  is_streaming?: boolean;

  // Aircraft only — the inputs to the camera footprint projection.
  // altitude_agl_m is height above the takeoff point, not MSL or terrain.
  altitude_agl_m?: number;
  gimbal_pitch_deg?: number;
  gimbal_yaw_deg?: number;
  gimbal_yaw_rel_deg?: number;
  gimbal_roll_deg?: number;
  gimbal_mode?: string;
  hfov_deg?: number;
  vfov_deg?: number;
  is_flying?: boolean;
  session_id?: string;

  // Aircraft flight state. Not inputs to the footprint — this is what the
  // operator reads off the aircraft HUD.
  vertical_speed_ms?: number;
  satellite_count?: number;
  home_latitude?: number;
  home_longitude?: number;
  platform?: string;
}

export interface SSEConnectionStatus {
  connected: boolean;
  lastEventAt: Date | null;
  eventsReceived: number;
  reconnectAttempts: number;
  error: string | null;
}

export type SSEEventHandler = (location: SSEDeviceLocation) => void;
export type SSEStatusHandler = (status: SSEConnectionStatus) => void;

class SSELocationService {
  private eventSource: EventSource | null = null;
  private baseUrl: string = process.env.REACT_APP_API_URL || '/api';
  private locationHandlers: Set<SSEEventHandler> = new Set();
  private statusHandlers: Set<SSEStatusHandler> = new Set();
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 5000; // Start at 5 seconds
  private reconnectTimer: NodeJS.Timeout | null = null;
  private lastErrorLogged: string | null = null; // Track last error to avoid spam
  
  private status: SSEConnectionStatus = {
    connected: false,
    lastEventAt: null,
    eventsReceived: 0,
    reconnectAttempts: 0,
    error: null
  };

  /**
   * Connect to SSE location stream
   * @param sourceFilter - Filter by source: 'phone' or 'vehicle'
   * @param deviceFilter - Filter by specific device ID
   * @param forceReconnect - Force a new connection even if one exists
   */
  connect(sourceFilter?: string, deviceFilter?: string, forceReconnect: boolean = false): void {
    // Check if already connected
    if (this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      if (!forceReconnect) {
        debugLog('sse', '[SSE] Already connected, use forceReconnect to reconnect');
        return;
      }
      // Force close existing connection
      debugLog('sse', '[SSE] Forcing reconnect...');
      this.eventSource.close();
      this.eventSource = null;
    }

    // Build URL with filters
    const params = new URLSearchParams();
    if (sourceFilter) params.append('source', sourceFilter);
    if (deviceFilter) params.append('device_id', deviceFilter);
    
    const queryString = params.toString();
    const url = withSseAccessToken(`${this.baseUrl}/sse/locations${queryString ? '?' + queryString : ''}`);
    
    debugLog('sse', '[SSE] Connecting to:', url);
    
    try {
      this.eventSource = new EventSource(url);
      
      // Connection opened
      this.eventSource.addEventListener('connected', (event: MessageEvent) => {
        debugLog('sse', '[SSE] Connected:', event.data);
        this.updateStatus({
          connected: true,
          error: null,
          reconnectAttempts: 0
        });
        this.reconnectAttempts = 0;
      });
      
      // Location update received
      this.eventSource.addEventListener('location', (event: MessageEvent) => {
        try {
          debugLog('sse', '[SSE] Raw location event:', event.data);
          const location: SSEDeviceLocation = JSON.parse(event.data);
          debugLog('sse', '[SSE] Parsed location:', location.device_id, location.entity_id);
          this.updateStatus({
            lastEventAt: new Date(),
            eventsReceived: this.status.eventsReceived + 1
          });
          
          debugLog('sse', `[SSE] Notifying ${this.locationHandlers.size} handlers`);
          // Notify all location handlers
          this.locationHandlers.forEach(handler => {
            try {
              handler(location);
            } catch (err) {
              console.error('[SSE] Handler error:', err);
            }
          });
        } catch (err) {
          console.error('[SSE] Failed to parse location:', err, 'Raw:', event.data);
        }
      });
      
      // Initial sync complete
      this.eventSource.addEventListener('initial_sync_complete', (event: MessageEvent) => {
        debugLog('sse', '[SSE] Initial sync complete:', event.data);
      });
      
      // Error event (server-sent error message)
      this.eventSource.addEventListener('error', (event: MessageEvent) => {
        const errorMsg = event.data || 'Unknown server error';
        // Only log if it's a different error (avoid spam)
        if (this.lastErrorLogged !== errorMsg) {
          this.lastErrorLogged = errorMsg;
          // Check if it's a Redis connection error (expected when Redis is down)
          if (errorMsg.includes('Redis') || errorMsg.includes('6379')) {
            console.warn('[SSE] Redis unavailable - real-time updates disabled. Using polling fallback.');
          } else {
            console.error('[SSE] Server error:', errorMsg);
          }
        }
        this.updateStatus({ error: errorMsg });
      });
      
      // Connection error (EventSource error)
      this.eventSource.onerror = () => {
        // Only log once per error type to avoid spam
        if (this.lastErrorLogged !== 'connection_lost') {
          this.lastErrorLogged = 'connection_lost';
          debugLog('sse', '[SSE] Connection error - will retry with backoff');
        }
        this.updateStatus({
          connected: false,
          error: 'Connection lost'
        });
        
        // Auto-reconnect
        this.scheduleReconnect(sourceFilter, deviceFilter);
      };
      
      // Connection opened
      this.eventSource.onopen = () => {
        debugLog('sse', '[SSE] Connection opened');
      };
      
    } catch (err) {
      console.error('[SSE] Failed to connect:', err);
      this.updateStatus({
        connected: false,
        error: 'Failed to connect'
      });
      this.scheduleReconnect(sourceFilter, deviceFilter);
    }
  }

  /**
   * Disconnect from SSE stream
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
      debugLog('sse', '[SSE] Disconnected');
    }
    
    this.updateStatus({
      connected: false,
      reconnectAttempts: 0
    });
  }

  /**
   * Schedule reconnection attempt with exponential backoff
   */
  private scheduleReconnect(sourceFilter?: string, deviceFilter?: string): void {
    if (this.reconnectTimer) {
      return; // Already scheduled
    }
    
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      // Only log once when max attempts reached
      if (this.lastErrorLogged !== 'max_attempts') {
        this.lastErrorLogged = 'max_attempts';
        console.warn('[SSE] Max reconnect attempts reached. Real-time updates disabled. Using polling fallback.');
      }
      this.updateStatus({
        error: 'Max reconnect attempts reached. Using polling fallback.'
      });
      return;
    }
    
    this.reconnectAttempts++;
    this.updateStatus({ reconnectAttempts: this.reconnectAttempts });
    
    // Exponential backoff: 5s, 7.5s, 11.25s, 16.9s, 25.3s
    const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts - 1);
    debugLog('sse', `[SSE] Reconnecting in ${Math.round(delay / 1000)}s (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      // Clear error log tracking to allow logging on new error types
      this.lastErrorLogged = null;
      this.connect(sourceFilter, deviceFilter);
    }, delay);
  }

  /**
   * Update status and notify handlers
   */
  private updateStatus(updates: Partial<SSEConnectionStatus>): void {
    this.status = { ...this.status, ...updates };
    this.statusHandlers.forEach(handler => handler(this.status));
  }

  /**
   * Subscribe to location updates
   */
  onLocation(handler: SSEEventHandler): () => void {
    this.locationHandlers.add(handler);
    return () => this.locationHandlers.delete(handler);
  }

  /**
   * Subscribe to status updates
   */
  onStatus(handler: SSEStatusHandler): () => void {
    this.statusHandlers.add(handler);
    // Send current status immediately
    handler(this.status);
    return () => this.statusHandlers.delete(handler);
  }

  /**
   * Get current connection status
   */
  getStatus(): SSEConnectionStatus {
    return { ...this.status };
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.status.connected;
  }

  /**
   * Get current locations from REST endpoint (non-streaming)
   * Use this for initial load before SSE connection
   */
  async getCurrentLocations(source?: string): Promise<{
    count: number;
    locations: SSEDeviceLocation[];
    timestamp: string;
  }> {
    const params = new URLSearchParams();
    if (source) params.append('source', source);
    
    const url = `${this.baseUrl}/sse/locations/current${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    return response.json();
  }

  /**
   * Get SSE service status (MQTT, Redis info)
   */
  async getServiceStatus(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/sse/status`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    return response.json();
  }
}

// Singleton instance
export const sseLocationService = new SSELocationService();
